import React from 'react';
import PropTypes from 'prop-types';
import isAfter from 'date-fns/is_after';

import StringConstants from 'app/shared/constants/StringConstants';

// Constellation Components
import { Text } from '@zillow/constellation';
import { Col, Link, Row } from '@zillow/zrs-comet';

import { Link as v4Link } from 'react-router-dom';

// rmw context
import { RMWContext } from 'app/shared/providers/RMWContext';

// Styled Components
import { StyledHR } from '../postingPath/UtilsStyledComponents';

// Stylesheets
import './style.scss';

const SECTION_HEADER_TITLE = 'Additional listing sources';
const DEMOTED_LISTING_COPY = 'We found a higher quality version of this listing in your account.' +
    ' Any edits made to this version may not be seen by renters on Zillow, Trulia and HotPads. ';

class DuplicateListingDetails extends React.Component {
    static contextType = RMWContext;

    static propTypes = {
        isFeedListing: PropTypes.bool,
        isTopDupe: PropTypes.bool,
        dupes: PropTypes.array,
        additionalListingSources: PropTypes.array,
        topDupeListingId: PropTypes.string,
    }

    static defaultProps = {
        isFeedListing: false,
        isTopDupe: false,
        dupes: [],
        additionalListingSources: [],
        topDupeListingId: '',
    }

    static contextTypes = {
        brand: PropTypes.string,
        paths: PropTypes.object,
    }

    constructor(props) {
        super(props);

        this.compareDates = this.compareDates.bind(this);
    }

    compareDates(first, second) {
        const firstDate = new Date(first.dateUpdated);
        const secondDate = new Date(second.dateUpdated);
        const isSecondMoreRecent = isAfter(secondDate, firstDate);

        return isSecondMoreRecent ? 1 : -1; // Return 1 to give second lower index, -1 to give first a lower index
    }

    render() {
        const { brand, paths } = this.context;
        const { dupes, isFeedListing, isTopDupe, additionalListingSources } = this.props;
        const { FORMATTED_BRAND_NAMES: brandConstants } = StringConstants;
        const sortedDupes = dupes.sort(this.compareDates);

        if (!isTopDupe) {
            const { topDupeListingId } = this.props;
            const link = `${paths.propertiesPage}/${topDupeListingId}/${paths.propertyDetailsListing}`;

            return (
                <div className="DuplicateListingDetails">
                    <Text
                        fontType="h4"
                        as="div"
                        marginBottom="xs"
                        mediaQuery={{
                            lg: {
                                marginBottom: 'sm',
                            },
                        }}
                        data-test="not-top-duplicate-title"
                    >
                        {SECTION_HEADER_TITLE}
                    </Text>
                    <Text fontType="body" className="DuplicateListingDetails-demoted-listing-text">
                        {DEMOTED_LISTING_COPY}
                        <Link reactRouterLink={v4Link} to={link}>
                            View higher quality listing.
                        </Link>
                    </Text>
                    <StyledHR
                        marginY="md"
                        marginX={0}
                    />
                </div>
            );
        }

        // If on feed listing, show latest manual listing link
        if (isFeedListing) {
            const dupeToShow = sortedDupes.find(dupe => dupe.feedName === brandConstants[brand]);
            if (!dupeToShow) {
                return null;
            }

            const { listingId } = dupeToShow;
            const link = `${paths.propertiesPage}/${listingId}/${paths.propertyDetailsListing}`;

            return (
                <div className="DuplicateListingDetails">
                    <Text
                        fontType="h4"
                        as="div"
                        marginBottom="xs"
                        mediaQuery={{
                            lg: {
                                marginBottom: 'sm',
                            },
                        }}
                        data-test="feed-duplicate-title"
                    >
                        {SECTION_HEADER_TITLE}
                    </Text>
                    <Text fontType="body">
                        Edit your
                        <Link reactRouterLink={v4Link} to={link}>
                            {` ${StringConstants.FORMATTED_BRAND_NAMES_RENTAL[brand]} listing.`}
                        </Link>
                    </Text>
                    <StyledHR
                        marginY="md"
                        marginX={0}
                    />
                </div>
            );
        }

        // If on manual listing, show list of feed sources
        if (additionalListingSources.length === 0) {
            return null;
        }
        const isSingleSource = additionalListingSources.length === 1;
        return (
            <div className="DuplicateListingDetails">
                <Row>
                    <Col xs={12}>
                        <Text
                            fontType="h4"
                            as="div"
                            marginBottom="xs"
                            mediaQuery={{
                                lg: {
                                    marginBottom: 'sm',
                                },
                            }}
                            data-test="duplicate-title"
                        >
                            {SECTION_HEADER_TITLE}
                        </Text>
                        <Text fontType="body" data-test="not-posted">
                            {`There ${isSingleSource ? 'is an alternative version' : 'are alternative versions'} of
                            this listing being received from the following source${isSingleSource ? '' : 's'},
                            but ${isSingleSource ? 'that version is' : 'those versions are'} not currently posted:`}
                        </Text>
                        <ul className="DuplicateListingDetails-list">
                            {additionalListingSources.sort().map((source, i) => <li key={i}>{source}</li>)}
                        </ul>
                        <StyledHR
                            marginY="md"
                            marginX={0}
                        />
                    </Col>
                </Row>
            </div>
        );
    }
}

export default DuplicateListingDetails;
